/**
 * RED-310 — Estonian number formatting for the analytics tables.
 *
 * "2 855,80" — space thousands, comma decimals, plain ASCII space so the
 * value survives copy-paste into Excel/Sheets and matches MaRu's own output
 * (and the server's `number_format`) byte-for-byte.
 */

/** Decimal places per result column (plan §"Frontend design"). */
export const DECIMALS = {
  count: 0,
  area_mean: 1,
  sum_total: 0,
  sum_min: 0,
  sum_max: 0,
  ppm_min: 2,
  ppm_max: 2,
  ppm_median: 2,
  ppm_mean: 2,
  ppm_sd: 2,
  change_pct_median: 1,
}

/** Placeholder for suppressed / missing cells. */
export const EMPTY_CELL = '–'

/**
 * Format a number the Estonian way ("12 345,67"): space every three integer
 * digits, comma decimal, fixed fraction digits. Hand-rolled instead of
 * `Intl.NumberFormat('et-EE')` because ICU's et locale only groups from five
 * digits ("5000" but "12 345") while the server's `number_format($v, $d, ',', ' ')`
 * — used for the summary sentence, footnotes and Excel — always groups; the
 * copy/paste table and the server texts must agree. Null/undefined/NaN → "".
 *
 * @param {number|string|null|undefined} value
 * @param {number} dp fraction digits (fixed)
 * @returns {string}
 */
export const formatEt = (value, dp = 0) => {
  if (value === null || value === undefined || value === '') return ''
  const number = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(number) || !Number.isFinite(number)) return ''
  const fixed = Math.abs(number).toFixed(dp)
  const [integer, fraction] = fixed.split('.')
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  const sign = number < 0 && Number(fixed) !== 0 ? '-' : ''
  return sign + grouped + (fraction ? `,${fraction}` : '')
}

/**
 * Format one table cell of a band / total row. Suppressed rows (n < 5) and
 * null cells render as "–"; the count column is never suppressed.
 *
 * @param {object} row band or total row from the API
 * @param {string} field column key (`count`, `area_mean`, `sum_total`, …)
 * @param {number} [dp] override the default decimals for the column
 * @returns {string}
 */
export const formatCell = (row, field, dp = DECIMALS[field] ?? 0) => {
  if (!row) return EMPTY_CELL
  const value = row[field]
  if (field !== 'count' && row.suppressed) return EMPTY_CELL
  if (value === null || value === undefined) return EMPTY_CELL
  return formatEt(value, dp)
}
